import type { paths } from '@ccp/api-client';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client.js';
import { toApiError } from '../../api/errors.js';

/**
 * The supervisor's boards, as the API answers them.
 *
 * The types come from the OpenAPI document, so a field a board reads is a field the API actually
 * returns; `generate-api-client --check` runs in CI and fails when the two drift.
 */
type Json<T> = T extends { content: { 'application/json': infer B } } ? B : never;

export type QueueBoard = Json<paths['/v1/supervision/queues']['get']['responses'][200]>;
export type QueueBoardRow = QueueBoard['items'][number];
export type AgentBoard = Json<paths['/v1/supervision/agents']['get']['responses'][200]>;
export type AgentBoardRow = AgentBoard['items'][number];
export type QueueInteractions = Json<
  paths['/v1/supervision/queues/{id}/interactions']['get']['responses'][200]
>;
export type SupervisionMode = 'listen' | 'whisper' | 'barge';

/**
 * How often a board re-reads itself.
 *
 * Two seconds, which is the dashboard staleness budget (LB-7). It is a **poll and not only a
 * subscription** on purpose: a compact notification carries no payload and the gateway can only
 * attribute an event to a queue when the queue *is* the aggregate, so an interaction being offered
 * out of a queue does not reach `queue:<id>`. The subscription makes the board jump when a queue or
 * a team itself changes; the poll is what keeps the counters inside the budget either way.
 *
 * TanStack Query does not poll a tab nobody is looking at (`refetchIntervalInBackground` is off by
 * default), so a supervisor with the board open in a background tab costs nothing.
 */
export const boardRefreshMs = 2_000;

/**
 * The query keys, chosen so the realtime client's invalidation reaches them.
 *
 * `['queues', …]` and `['teams', …]` are what `realtime/apply-event.ts` invalidates for a Queue or a
 * Team aggregate, and a prefix match is what React Query does — so a board keyed under those prefixes
 * is repaired by the same one line that repairs every other queue query.
 */
export const supervisionKeys = {
  queueBoard: ['queues', 'board'] as const,
  agentBoard: ['teams', 'board'] as const,
  queueInteractions: (queueId: string) => ['queues', queueId, 'waiting'] as const,
};

export function queueBoardQueryOptions() {
  return queryOptions({
    queryKey: supervisionKeys.queueBoard,
    refetchInterval: boardRefreshMs,
    queryFn: async (): Promise<QueueBoard> => {
      const { data, response } = await api.GET('/v1/supervision/queues', {});
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
  });
}

export function useQueueBoard() {
  return useQuery(queueBoardQueryOptions());
}

export function agentBoardQueryOptions() {
  return queryOptions({
    queryKey: supervisionKeys.agentBoard,
    refetchInterval: boardRefreshMs,
    queryFn: async (): Promise<AgentBoard> => {
      const { data, response } = await api.GET('/v1/supervision/agents', {});
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
  });
}

export function useAgentBoard() {
  return useQuery(agentBoardQueryOptions());
}

/** Who is waiting in one queue. Read only while the drill-down is open. */
export function useQueueInteractions(queueId: string | undefined) {
  return useQuery({
    queryKey: supervisionKeys.queueInteractions(queueId ?? 'none'),
    enabled: queueId !== undefined,
    refetchInterval: boardRefreshMs,
    queryFn: async (): Promise<QueueInteractions> => {
      const { data, response } = await api.GET('/v1/supervision/queues/{id}/interactions', {
        params: { path: { id: queueId as string } },
      });
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
  });
}

/**
 * Listen to, whisper into, or barge a live call.
 *
 * Nothing is applied optimistically and nothing is shown as "listening": what this returns is that
 * the platform accepted the request (202), and the supervisor finds out it worked when their phone
 * rings. Claiming otherwise would be the interface telling them they are on a call they are not on.
 *
 * A refusal is a state to display. The API authorises this per call — the supervisor's teams and
 * queues against the call's — so a 403 here is the boundary doing its job and not a bug.
 */
export function useSuperviseCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly interactionId: string;
      readonly mode: SupervisionMode;
    }) => {
      const { data, response } = await api.POST('/v1/interactions/{id}/supervise', {
        params: { path: { id: input.interactionId } },
        body: { mode: input.mode },
      });
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
    // The boards show what each agent is on, and supervision does not change that — but the
    // interaction's own stream does, and reading it back is cheap next to a wrong board.
    onSuccess: (_result, input) =>
      queryClient.invalidateQueries({ queryKey: ['interactions', input.interactionId] }),
  });
}
